import { Check, X, Loader2, Pause, MessageCircle } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { approveWorkflowRun, rejectWorkflowRun } from '@/lib/api';
import { useWorkflowStore, type ApprovalGateState } from '@/stores/workflow-store';

export interface ApprovalGateData {
  runId: string;
  nodeId: string;
  message: string;
  captureResponse: boolean;
  hasOnReject: boolean;
  resolved?: 'approved' | 'rejected';
}

interface QuickRepliesProps {
  /** Active approval gate for the conversation, if any */
  approvalGate?: ApprovalGateData;
  /** Conversation ID for resolving approval gates */
  conversationId?: string;
  /** Suggested reply labels to render as buttons */
  suggestedReplies?: string[];
  /** Called when user clicks a suggested reply */
  onSuggestedReply?: (message: string) => void;
}

export function QuickReplies({
  approvalGate,
  conversationId,
  suggestedReplies,
  onSuggestedReply,
}: QuickRepliesProps): ReactElement | null {
  const hasApproval = approvalGate && !approvalGate.resolved;
  const hasReplies = suggestedReplies && suggestedReplies.length > 0;
  const hasResolvedGate = !!approvalGate?.resolved;

  if (!hasApproval && !hasReplies && !hasResolvedGate) {
    return null;
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 pb-2">
      {/* An approval gate always belongs to a conversation; only render the buttons
          when conversationId is present so resolveGate is never silently skipped. */}
      {approvalGate && conversationId && (
        <ApprovalButtons gate={approvalGate} conversationId={conversationId} />
      )}
      {hasReplies && onSuggestedReply && (
        <div className="flex flex-wrap gap-2">
          {suggestedReplies.map(reply => (
            <Button
              key={reply}
              variant="outline"
              size="sm"
              onClick={(): void => {
                onSuggestedReply(reply);
              }}
              className="flex items-center gap-1.5"
            >
              <MessageCircle className="h-3 w-3" />
              {reply}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ApprovalButtons({
  gate,
  conversationId,
}: {
  gate: ApprovalGateState;
  conversationId: string;
}): ReactElement {
  const [comment, setComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const resolveGate = useWorkflowStore(s => s.resolveApprovalGate);

  const approveMutation = useMutation({
    mutationFn: () => approveWorkflowRun(gate.runId, comment || undefined),
    onSuccess: () => {
      resolveGate(conversationId, 'approved');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectWorkflowRun(gate.runId, rejectReason || undefined),
    onSuccess: () => {
      resolveGate(conversationId, 'rejected');
    },
  });

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  if (gate.resolved) {
    return (
      <div className="rounded-lg border border-border bg-surface-elevated px-4 py-3">
        <div className="flex items-center gap-2 text-sm">
          {gate.resolved === 'approved' ? (
            <>
              <Check className="h-4 w-4 text-success" />
              <span className="text-success font-medium">Approved</span>
            </>
          ) : (
            <>
              <X className="h-4 w-4 text-error" />
              <span className="text-error font-medium">Rejected</span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-warning">
        <Pause className="h-4 w-4" />
        Approval Required
      </div>
      <p className="text-sm text-text-primary">{gate.message}</p>

      {gate.captureResponse && (
        <Input
          placeholder="Comment (optional)"
          value={comment}
          onChange={e => {
            setComment(e.target.value);
          }}
          disabled={isPending}
          className="text-sm"
        />
      )}

      {showReject && (
        <div className="space-y-2">
          <Input
            placeholder="Rejection reason (optional)"
            value={rejectReason}
            onChange={e => {
              setRejectReason(e.target.value);
            }}
            disabled={isPending}
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                rejectMutation.mutate();
              }}
              disabled={isPending}
            >
              {rejectMutation.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <X className="mr-1 h-3 w-3" />
              )}
              Confirm Reject
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowReject(false);
              }}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!showReject && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => {
              approveMutation.mutate();
            }}
            disabled={isPending}
            className="bg-success hover:bg-success/90"
          >
            {approveMutation.isPending ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Check className="mr-1 h-3 w-3" />
            )}
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setShowReject(true);
            }}
            disabled={isPending}
          >
            <X className="mr-1 h-3 w-3" />
            Reject
          </Button>
        </div>
      )}

      {(approveMutation.isError || rejectMutation.isError) && (
        <p className="text-xs text-error">
          {approveMutation.error instanceof Error
            ? approveMutation.error.message
            : rejectMutation.error instanceof Error
              ? rejectMutation.error.message
              : 'Action failed'}
        </p>
      )}
    </div>
  );
}
